import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit, FiEye, FiLink, FiLock, FiPlus, FiRefreshCw, FiSave, FiSearch, FiUnlock, FiUsers, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { useAuth } from '../../provider/auth.provider';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import DragDropFileInput from '../../components/DragDropFileInput';
import { useDebounce } from '../../hooks/useDebounce';

interface RoleInfo {
  id: number;
  name: string;
}

interface UserDetails {
  id?: number;
  user_id?: number;
  date_of_joining?: string | null;
  portal_access_status?: 'active' | 'inactive' | 'suspended' | null;
  date_of_leaving?: string | null;
  alternate_mobile_number?: string | null;
  address_permanent?: string | null;
  marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | null;
  education?: string | null;
  email_company?: string | null;
  aadhar_card_number?: string | null;
  pan_card_number?: string | null;
  licence_number?: string | null;
  emergency_mobile_number?: string | null;
  blood_group?: string | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  ifsc_code?: string | null;
  esi_number?: string | null;
  esi_tic_attachment?: string | null;
  pf_number?: string | null;
  payment_qr_code?: string | null;
  referred_by?: string | null;
  father_name?: string | null;
  father_aadhar_card_number?: string | null;
  mother_name?: string | null;
  mother_aadhar_card_number?: string | null;
  spouse_name?: string | null;
  spouse_aadhar_card_number?: string | null;
  child_name_1?: string | null;
  child_name_2?: string | null;
  family_picture?: string | null;
  bank_passbook?: string | null;
  marksheet?: string | null;
  aadhar_card_document?: string | null;
  pan_card_document?: string | null;
  driving_licence_document?: string | null;
  employee_id_card_temporary?: string | null;
  employee_id_card_permanent?: string | null;
  employee_id_card_permanent_back?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface EditToken {
  id: number;
  section: string;
  granted_by_user_id: number;
  granted_at: string;
  granter?: { id: number; first_name: string; last_name: string; username: string };
}

interface UserRow {
  id: number;
  employee_code?: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: RoleInfo;
  user_details?: UserDetails | null;
  devices?: { app_version?: string, device_name?: string }[];
}

interface UsersResponse {
  users: UserRow[];
  pagination?: {
    total_items?: number | string;
    total_count?: number | string;
    current_page?: number | string;
    page?: number | string;
    total_pages?: number | string;
    items_per_page?: number | string;
    limit?: number | string;
    has_next_page?: boolean;
    has_previous_page?: boolean;
    has_prev_page?: boolean;
  };
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

type DrawerMode = 'view' | 'add' | 'edit';
type UserDetailsFormData = Partial<UserDetails>;
type FieldType = 'text' | 'date' | 'email' | 'tel' | 'textarea' | 'select';

interface FieldConfig {
  key: keyof UserDetailsFormData;
  label: string;
  type: FieldType;
  maxLength?: number;
  transform?: 'upper';
  options?: Array<{ value: string; label: string }>;
  colSpan?: 'single' | 'double';
}

const BLOOD_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(v => ({ value: v, label: v }));
const MARITAL_OPTIONS = [
  { value: 'single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'divorced', label: 'Divorced' }, { value: 'widowed', label: 'Widowed' }
];
const PORTAL_OPTIONS = [
  { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'suspended', label: 'Suspended' }
];

const SECTION_FIELDS: Array<{ title: string; actionKey: string; fields: FieldConfig[] }> = [
  {
    title: 'Employment Information',
    actionKey: 'employment',
    fields: [
      { key: 'date_of_joining', label: 'Date of Joining', type: 'date' },
      { key: 'portal_access_status', label: 'Portal Access Status', type: 'select', options: PORTAL_OPTIONS },
      { key: 'date_of_leaving', label: 'Date of Leaving', type: 'date' },
      { key: 'email_company', label: 'Company Email', type: 'email' },
      { key: 'referred_by', label: 'Referred By', type: 'text' }
    ]
  },
  {
    title: 'Personal Information',
    actionKey: 'personal',
    fields: [
      { key: 'alternate_mobile_number', label: 'Alternate Mobile', type: 'tel' },
      { key: 'emergency_mobile_number', label: 'Emergency Contact', type: 'tel' },
      { key: 'blood_group', label: 'Blood Group', type: 'select', options: BLOOD_OPTIONS },
      { key: 'marital_status', label: 'Marital Status', type: 'select', options: MARITAL_OPTIONS },
      { key: 'education', label: 'Education', type: 'text' },
      { key: 'address_permanent', label: 'Permanent Address', type: 'textarea', colSpan: 'double' }
    ]
  },
  {
    title: 'Identity Documents',
    actionKey: 'identity',
    fields: [
      { key: 'aadhar_card_number', label: 'Aadhar Card Number', type: 'text', maxLength: 12 },
      { key: 'licence_number', label: 'Driving Licence Number', type: 'text' }
    ]
  },
  {
    title: 'Bank & Statutory Information',
    actionKey: 'bank',
    fields: [
      { key: 'pan_card_number', label: 'PAN Card Number', type: 'text', maxLength: 10, transform: 'upper' },
      { key: 'bank_name', label: 'Bank Name', type: 'text' },
      { key: 'bank_account_number', label: 'Account Number', type: 'text' },
      { key: 'ifsc_code', label: 'IFSC Code', type: 'text', maxLength: 11, transform: 'upper' },
      { key: 'esi_number', label: 'ESI Number', type: 'text' },
      { key: 'pf_number', label: 'PF Number', type: 'text' }
    ]
  },
  {
    title: 'Family Information',
    actionKey: 'family',
    fields: [
      { key: 'father_name', label: "Father's Name", type: 'text' },
      { key: 'father_aadhar_card_number', label: "Father's Aadhar", type: 'text', maxLength: 12 },
      { key: 'mother_name', label: "Mother's Name", type: 'text' },
      { key: 'mother_aadhar_card_number', label: "Mother's Aadhar", type: 'text', maxLength: 12 },
      { key: 'spouse_name', label: "Spouse's Name", type: 'text' },
      { key: 'spouse_aadhar_card_number', label: "Spouse's Aadhar", type: 'text', maxLength: 12 },
      { key: 'child_name_1', label: 'Child 1 Name', type: 'text' },
      { key: 'child_name_2', label: 'Child 2 Name', type: 'text' }
    ]
  }
];

const DOCUMENT_SECTION_ACTION_KEY = 'documents';

const DOCUMENT_FIELDS: Array<{ key: keyof UserDetailsFormData; label: string }> = [
  { key: 'aadhar_card_document', label: 'Aadhar Card' },
  { key: 'driving_licence_document', label: 'Driving Licence' },
  { key: 'pan_card_document', label: 'PAN Card' },
  { key: 'bank_passbook', label: 'Bank Passbook' },
  { key: 'marksheet', label: 'Marksheet' },
  { key: 'esi_tic_attachment', label: 'ESI TIC' },
  { key: 'family_picture', label: 'Family Picture' },
  { key: 'employee_id_card_temporary', label: 'Temp ID Card' },
  { key: 'employee_id_card_permanent', label: 'Perm ID Card Front' },
  { key: 'employee_id_card_permanent_back', label: 'Perm ID Card Back' }
];

const HrUserDetailsPage: React.FC = () => {
  const { hasPermission, user: currentUser } = useAuth();
  const canViewDetails = hasPermission('user_details', 'view');
  const canCreateDetails = hasPermission('user_details', 'create');
  const canUpdateDetails = hasPermission('user_details', 'update');
  const canGrantSectionEdit = hasPermission('user_details', 'grant_section_edit');

  const [users, setUsers] = useState<UserRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: 10, has_next_page: false, has_previous_page: false });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounced search — auto-fires after 400 ms; manual Search button still works too
  const debouncedSearch = useDebounce(search, 400);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('view');
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [formData, setFormData] = useState<UserDetailsFormData>({});
  const [detailsFiles, setDetailsFiles] = useState<Record<string, File | null>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTokens, setEditTokens] = useState<EditToken[]>([]);
  const [grantingSection, setGrantingSection] = useState<string | null>(null);

  const drawerReadOnly = drawerMode === 'view';
  const drawerTitle = useMemo(() => drawerMode === 'add' ? 'Add Extended Details' : drawerMode === 'edit' ? 'Edit Extended Details' : 'View Extended Details', [drawerMode]);

  const normalizePagination = (api: UsersResponse['pagination'], targetPage: number, targetLimit: number, count: number): NormalizedPagination => {
    const current = Number(api?.current_page ?? api?.page) || targetPage || 1;
    const limit = Number(api?.items_per_page ?? api?.limit) || targetLimit || 10;
    const total = Number(api?.total_items ?? api?.total_count) || count || 0;
    const pages = Number(api?.total_pages) || Math.max(1, Math.ceil(total / limit));
    return {
      total_items: total,
      current_page: current,
      total_pages: pages,
      items_per_page: limit,
      has_next_page: typeof api?.has_next_page === 'boolean' ? api.has_next_page : current < pages,
      has_previous_page: typeof api?.has_previous_page === 'boolean' ? api.has_previous_page : typeof api?.has_prev_page === 'boolean' ? api.has_prev_page : current > 1
    };
  };

  const fetchUsers = async (targetPage: number = page, targetLimit: number = pagination.items_per_page) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/users/list-with-details', { params: { page: targetPage, limit: targetLimit, search: debouncedSearch.trim() || undefined } });
      if (response.data.success) {
        const payload: UsersResponse = response.data.data || { users: [] };
        const rows = payload.users || [];
        setUsers(rows);
        setPagination(normalizePagination(payload.pagination, targetPage, targetLimit, rows.length));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch users with details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(page); }, [page]);

  // Auto-search when debounced value changes (resets to page 1)
  useEffect(() => {
    setPage(1);
    fetchUsers(1, pagination.items_per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null
  };

  const fetchEditTokens = async (userId: number) => {
    try {
      const resp = await axiosInstance.get(`/user-details/${userId}/edit-tokens`);
      setEditTokens(resp.data?.data?.tokens || []);
    } catch {
      setEditTokens([]);
    }
  };

  const handleGrantSectionEdit = async (section: string) => {
    if (!selectedUser) return;
    try {
      setGrantingSection(section);
      await axiosInstance.post(`/user-details/${selectedUser.id}/grant-section-edit`, { section });
      toast.success(`Edit permission granted for ${section}`);
      await fetchEditTokens(selectedUser.id);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to grant permission');
    } finally {
      setGrantingSection(null);
    }
  };

  const handleRevokeToken = async (tokenId: number) => {
    if (!selectedUser) return;
    try {
      await axiosInstance.delete(`/user-details/${selectedUser.id}/edit-tokens/${tokenId}`);
      toast.success('Edit permission revoked');
      setEditTokens(prev => prev.filter(t => t.id !== tokenId));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to revoke permission');
    }
  };

  const fetchUserDetails = async (userId: number) => {
    try {
      setDetailsLoading(true);
      const response = await axiosInstance.get(`/user-details/${userId}/details`);
      setFormData(response?.data?.data?.user_details || {});
    } catch (error: any) {
      if (error?.response?.status !== 404) console.error(error);
      setFormData({});
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedUser || refreshing || detailsLoading) return;
    try {
      setRefreshing(true);
      await fetchUserDetails(selectedUser.id);
      if (drawerMode === 'edit' || canGrantSectionEdit) await fetchEditTokens(selectedUser.id);
      toast.success('Details refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  const openDrawer = async (mode: DrawerMode, user: UserRow) => {
    setSelectedUser(user);
    setDrawerMode(mode);
    setFormData(user.user_details || {});
    setDetailsFiles({});
    setEditTokens([]);
    setIsDrawerOpen(true);
    await fetchUserDetails(user.id);
    if (mode === 'edit') await fetchEditTokens(user.id);
    else if (canGrantSectionEdit && mode !== 'add') await fetchEditTokens(user.id);
  };

  const handleInput = (field: keyof UserDetailsFormData, value: string, transform?: 'upper') => setFormData(prev => ({ ...prev, [field]: (transform === 'upper' ? value.toUpperCase() : value) || null }));
  const handleFileChange = (field: string, file: File | null) => setDetailsFiles(prev => ({ ...prev, [field]: file }));

  const handleSave = async () => {
    if (!selectedUser) return;
    try {
      setSaving(true);
      const multipart = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (['id', 'user_id', 'created_at', 'updated_at'].includes(key)) return;
        if (value !== undefined && value !== null && value !== '') multipart.append(key, String(value));
      });
      Object.entries(detailsFiles).forEach(([key, file]) => { if (file) multipart.append(key, file); });
      await axiosInstance.post(`/user-details/${selectedUser.id}/details`, multipart, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(drawerMode === 'add' ? 'Details added successfully' : 'Details updated successfully');
      setIsDrawerOpen(false);
      fetchUsers(page, pagination.items_per_page);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: FieldConfig, isReadOnly: boolean = drawerReadOnly) => {
    const value = ((formData[f.key] as string) || '');
    const base = 'w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100';
    if (f.type === 'textarea') {
      return <textarea disabled={isReadOnly} value={value} onChange={(e) => handleInput(f.key, e.target.value, f.transform)} rows={2} className={base} />;
    }
    if (f.type === 'select') {
      return (
        <select disabled={isReadOnly} value={value} onChange={(e) => handleInput(f.key, e.target.value, f.transform)} className={base}>
          <option value="">Select</option>
          {(f.options || []).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    return <input type={f.type} disabled={isReadOnly} value={f.type === 'date' ? value.split('T')[0] : value} onChange={(e) => handleInput(f.key, e.target.value, f.transform)} maxLength={f.maxLength} className={base} />;
  };

  return (
    <>
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">HR Mgmt - User Details</h1>
            <p className="text-sm text-gray-600 mt-1">Manage employee extended details in a separate module.</p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name / email / username" className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <button onClick={() => { setPage(1); fetchUsers(1, pagination.items_per_page); }} className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">Search</button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Employee Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">App Version</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Details Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-600">Loading users...</td></tr> :
                users.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-600">No users found.</td></tr> :
                  users.map((u) => {
                    const hasDetails = Boolean(u.user_details?.id);
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3"><div className="flex items-center gap-2">
                          {hasDetails && canViewDetails && <button onClick={() => openDrawer('view', u)} className="p-2 rounded hover:bg-gray-100 text-gray-700"><FiEye className="w-4 h-4" /></button>}
                          {hasDetails && canUpdateDetails && <button onClick={() => openDrawer('edit', u)} className="p-2 rounded hover:bg-blue-50 text-blue-700"><FiEdit className="w-4 h-4" /></button>}
                          {!hasDetails && canCreateDetails && <button onClick={() => openDrawer('add', u)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-800 text-white text-xs hover:bg-blue-900"><FiPlus className="w-3 h-3" />Add</button>}
                        </div></td>
                        <td className="px-4 py-3 text-sm text-gray-900">{u.first_name} {u.last_name}<div className="text-xs text-gray-500">@{u.username}</div></td>
                        <td className="px-4 py-3 text-sm text-gray-700">{u.employee_code || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{u.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{u.role?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{u.devices?.[0]?.app_version || '-'}</td>
                        <td className="px-4 py-3 text-sm"><span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${hasDetails ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{hasDetails ? 'Added' : 'Missing'}</span></td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => setPage(nextPage)}
          onLimitChange={(nextLimit) => {
            setPagination(prev => ({ ...prev, items_per_page: nextLimit }));
            setPage(1);
            if (page === 1) fetchUsers(1, nextLimit);
          }}
        />
      </div>

      
    </div>
    {isDrawerOpen && selectedUser && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-all duration-300 opacity-100" onClick={() => !saving && setIsDrawerOpen(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white border-l border-gray-200 shadow-xl flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{drawerTitle}</h3>
                <p className="text-xs text-gray-500 mt-1">{selectedUser.first_name} {selectedUser.last_name} ({selectedUser.employee_code || 'No Code'})</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="p-2 hover:bg-gray-100 rounded disabled:opacity-40"
                  onClick={handleRefresh}
                  disabled={saving || refreshing || detailsLoading}
                  title="Refresh"
                >
                  <FiRefreshCw className={`w-4 h-4 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded" onClick={() => setIsDrawerOpen(false)} disabled={saving}><FiX className="w-5 h-5 text-gray-600" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {detailsLoading ? (
                <div className="py-10 text-center text-sm text-gray-600">Loading extended details...</div>
              ) : (
                <>
                  {SECTION_FIELDS.map(section => {
                    const canViewSection = hasPermission('user_details', `view_${section.actionKey}`);
                    const canEditSection = hasPermission('user_details', `edit_${section.actionKey}`);
                    const canCreateSection = hasPermission('user_details', `create_${section.actionKey}`);
                    const isAddMode = drawerMode === 'add';
                    const isOwnProfile = currentUser?.id === selectedUser?.id;
                    const sectionToken = editTokens.find(t => t.section === section.actionKey);
                    const hasToken = Boolean(sectionToken);
                    // Own-profile edit: allowed only if they have a token; staff with edit perm: always allowed
                    const sectionEditable = canEditSection || (isOwnProfile && hasToken);
                    if (isAddMode && !canCreateSection) return null;
                    if (!isAddMode && !canViewSection && !sectionEditable && !canGrantSectionEdit) return null;
                    const sectionReadOnly = drawerReadOnly || (isAddMode ? false : !sectionEditable);
                    return (
                      <div key={section.title}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">{section.title}</h4>
                            {drawerMode === 'edit' && !isAddMode && (
                              sectionEditable
                                ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><FiUnlock className="w-3 h-3" />Editable</span>
                                : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><FiLock className="w-3 h-3" />Locked</span>
                            )}
                          </div>
                          {drawerMode !== 'add' && canGrantSectionEdit && (
                            <div className="flex items-center gap-2">
                              {hasToken && sectionToken && (
                                <span className="text-xs text-green-600 mr-1">
                                  Granted by {sectionToken.granter?.first_name || sectionToken.granter?.username || 'Admin'}
                                </span>
                              )}
                              <button
                                onClick={() => hasToken && sectionToken ? handleRevokeToken(sectionToken.id) : handleGrantSectionEdit(section.actionKey)}
                                disabled={grantingSection === section.actionKey}
                                className={`px-2 py-1 text-xs rounded border ${hasToken ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-blue-300 text-blue-700 hover:bg-blue-50'} disabled:opacity-50`}
                              >
                                {grantingSection === section.actionKey ? 'Granting...' : hasToken ? 'Revoke Edit' : 'Grant Edit'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {section.fields.map(field => (
                            <div key={String(field.key)} className={field.colSpan === 'double' ? 'sm:col-span-2' : ''}>
                              <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                              {renderField(field, sectionReadOnly)}
                            </div>
                          ))}
                          {section.actionKey === 'bank' && (
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Payment QR Code</label>
                              {sectionReadOnly ? (
                                formData.payment_qr_code
                                  ? <div className="flex flex-col items-start gap-2">
                                      <img src={formData.payment_qr_code} alt="Payment QR Code" className="w-32 h-32 object-contain border border-gray-200 rounded p-1 bg-white" />
                                      <a href={formData.payment_qr_code} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800"><FiLink className="w-3 h-3" />View Full Size</a>
                                    </div>
                                  : <p className="text-sm text-gray-500">Not uploaded</p>
                              ) : (
                                <DragDropFileInput
                                  label="QR Code Image"
                                  onFileSelect={(file) => handleFileChange('payment_qr_code', file)}
                                  selectedFile={detailsFiles['payment_qr_code'] || null}
                                  existingFile={formData.payment_qr_code || null}
                                  accept="image/*"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {(() => {
                    const isAddMode = drawerMode === 'add';
                    const isOwnProfile = currentUser?.id === selectedUser?.id;
                    const canViewDocs = hasPermission('user_details', `view_${DOCUMENT_SECTION_ACTION_KEY}`);
                    const canEditDocs = hasPermission('user_details', `edit_${DOCUMENT_SECTION_ACTION_KEY}`);
                    const canCreateDocs = hasPermission('user_details', `create_${DOCUMENT_SECTION_ACTION_KEY}`);
                    const docsToken = editTokens.find(t => t.section === DOCUMENT_SECTION_ACTION_KEY);
                    const hasDocsToken = Boolean(docsToken);
                    const docsEditable = canEditDocs || (isOwnProfile && hasDocsToken);
                    if (isAddMode && !canCreateDocs) return null;
                    if (!isAddMode && !canViewDocs && !docsEditable && !canGrantSectionEdit) return null;
                    const docsReadOnly = drawerReadOnly || (isAddMode ? false : !docsEditable);
                    return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">Document Uploads</h4>
                        {drawerMode === 'edit' && (
                          docsEditable
                            ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><FiUnlock className="w-3 h-3" />Editable</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><FiLock className="w-3 h-3" />Locked</span>
                        )}
                      </div>
                      {drawerMode !== 'add' && canGrantSectionEdit && (
                        <div className="flex items-center gap-2">
                          {hasDocsToken && docsToken && (
                            <span className="text-xs text-green-600 mr-1">
                              Granted by {docsToken.granter?.first_name || docsToken.granter?.username || 'Admin'}
                            </span>
                          )}
                          <button
                            onClick={() => hasDocsToken && docsToken ? handleRevokeToken(docsToken.id) : handleGrantSectionEdit(DOCUMENT_SECTION_ACTION_KEY)}
                            disabled={grantingSection === DOCUMENT_SECTION_ACTION_KEY}
                            className={`px-2 py-1 text-xs rounded border ${hasDocsToken ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-blue-300 text-blue-700 hover:bg-blue-50'} disabled:opacity-50`}
                          >
                            {grantingSection === DOCUMENT_SECTION_ACTION_KEY ? 'Granting...' : hasDocsToken ? 'Revoke Edit' : 'Grant Edit'}
                          </button>
                        </div>
                      )}
                    </div>
                    {docsReadOnly ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {DOCUMENT_FIELDS.map((doc) => {
                          const url = (formData[doc.key] as string) || '';
                          return (
                            <div key={String(doc.key)} className="border border-gray-200 rounded p-3">
                              <p className="text-xs font-medium text-gray-700 mb-2">{doc.label}</p>
                              {url ? <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-800"><FiLink className="w-4 h-4" />View Document</a> : <p className="text-sm text-gray-500">Not uploaded</p>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {DOCUMENT_FIELDS.map((doc) => (
                          <DragDropFileInput key={String(doc.key)} label={doc.label} onFileSelect={(file) => handleFileChange(String(doc.key), file)} selectedFile={detailsFiles[String(doc.key)] || null} existingFile={(formData[doc.key] as string) || null} accept="image/*,.pdf" />
                        ))}
                      </div>
                    )}
                  </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="inline-flex items-center gap-2 text-xs text-gray-600"><FiUsers className="w-4 h-4" />Resource: <span className="font-semibold">user_details</span></div>
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 text-sm border border-gray-300 rounded" onClick={() => setIsDrawerOpen(false)} disabled={saving}>Close</button>
                {!drawerReadOnly && <button onClick={handleSave} disabled={saving || detailsLoading} className="px-4 py-2 text-sm bg-blue-800 text-white rounded disabled:opacity-50 inline-flex items-center gap-2"><FiSave className="w-4 h-4" />{saving ? 'Saving...' : drawerMode === 'add' ? 'Add Details' : 'Update Details'}</button>}
              </div>
            </div>
          </div>
        </>
      )}
      </>
  );
};

export default HrUserDetailsPage;
